import React, { useRef, useEffect, useContext } from 'react';
import {
  Button,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment
} from '@mui/material';
import { Button as BootstrapButton, Spinner } from 'react-bootstrap';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SaveIcon from '@mui/icons-material/Save';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import QuestionnaireContext from './QuestionnaireContext';
import { validateTextInput } from '../common/TextValidator';
import questions from './questions';
import SuggestionCard from '../explore_page/SuggestionCard';
import '../../styles/components/explore_page/Questionnaire.css';

const QuestionnaireForm = ({
  mode,
  onSubmit,
  onSecondaryAction,
  onReset,
  onAddCustomOption,
  onDeleteCustomOption,
  onUpdateAnswers,
  canSubmit,
  loading,
  errorMessage,
  answers,
  customOptions,
  addingCustomOption,
  customOptionInput,
  validationError,
  customPromptInput,
  showCustomPrompt,
  customPromptError,
  startDate,
  endDate,
  isMobile,
  handleCustomPromptChange,
  handleToggleCustomPrompt,
  handleStartAddingOption,
  handleCustomOptionInputChange,
  handleDateRangeChange,
  handlePromptValidationError,
  toggleMapVisibility,
  isMapVisible,
  hasSuggestions,
  onClose,
  suggestions,
  suggestionSectionRef
}) => {
  const questionnaireRef = useRef(null);
  const { scrollPositionRef } = useContext(QuestionnaireContext);

  useEffect(() => {
    if (questionnaireRef.current) {
      questionnaireRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [scrollPositionRef]);

  const handleScroll = () => {
    if (questionnaireRef.current) {
      scrollPositionRef.current = questionnaireRef.current.scrollTop;
    }
  };

  const handleButtonClick = (questionId, option, type) => {
    onUpdateAnswers(questionId, option, type);
  };

  return (
    <div className="questionnaire-container" ref={questionnaireRef} onScroll={handleScroll}>
      {mode === 'EXPLORE' && (
        <div className="title-section">
          <Typography variant="h3" className="main-title">
            Find Your Next Adventure!
          </Typography>
        </div>
      )}
      <form onSubmit={onSubmit}>
        {questions.map((q) => {
          const currentCustomOptions = customOptions[q.id] || [];
          const totalCustomOptions = currentCustomOptions.length;
          const canAddMore = totalCustomOptions < 5;
          return (
            <div key={q.id} className="question-section">
              <Typography variant="subtitle1" gutterBottom className="question-text">
                {q.question}
              </Typography>
              {(q.type === 'multi-select' || q.type === 'single-select') && (
                <>
                  <div className="options-container">
                    {q.options.map((option) => (
                      <div className="option-item" key={option}>
                        <Button
                          variant={
                            (q.type === 'multi-select' && answers[q.id]?.includes(option)) ||
                            (q.type === 'single-select' && answers[q.id] === option)
                              ? 'contained'
                              : 'outlined'
                          }
                          color="primary"
                          onClick={() => handleButtonClick(q.id, option, q.type)}
                        >
                          {option}
                        </Button>
                      </div>
                    ))}
                    {currentCustomOptions.map((option) => (
                      <div className="option-item" key={option}>
                        <Button
                          variant={
                            (q.type === 'multi-select' && answers[q.id]?.includes(option)) ||
                            (q.type === 'single-select' && answers[q.id] === option)
                              ? 'contained'
                              : 'outlined'
                          }
                          color="primary"
                          onClick={() => handleButtonClick(q.id, option, q.type)}
                          className="custom-option-button"
                        >
                          <span className="custom-option-text">{option}</span>
                          <span
                            className="delete-custom-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteCustomOption(q.id, option);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </span>
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="add-custom-option-section">
                    {addingCustomOption === q.id ? (
                      <div className="add-custom-option-input">
                        <div className="custom-option-input-wrapper">
                          <TextField
                            variant="outlined"
                            size="small"
                            value={customOptionInput}
                            onChange={handleCustomOptionInputChange}
                            placeholder="Enter custom option"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                onAddCustomOption(q.id);
                              }
                            }}
                            inputProps={{ maxLength: 25 }}
                            fullWidth
                            error={!!validationError}
                            helperText={validationError}
                            className="custom-option-input-field"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <CloseIcon
                                    fontSize="small"
                                    className="custom-option-clear"
                                    onClick={() => {
                                      handlePromptValidationError('');
                                      handleStartAddingOption(null);
                                    }}
                                  />
                                </InputAdornment>
                              )
                            }}
                          />
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => onAddCustomOption(q.id)}
                            className="custom-option-add-button"
                          >
                            Add
                          </Button>
                        </div>
                      </div>
                    ) : (
                      canAddMore && (
                        <Button
                          variant="text"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={() => handleStartAddingOption(q.id)}
                          className="add-custom-option-button"
                        >
                          Add
                        </Button>
                      )
                    )}
                  </div>
                </>
              )}
              {q.type === 'date-range' && (
                <div className="date-range-section">
                  <div className="date-picker-wrapper">
                    <ReactDatePicker
                      selectsRange
                      startDate={startDate}
                      endDate={endDate}
                      onChange={handleDateRangeChange}
                      isClearable
                      placeholderText="Select a Range"
                      withPortal
                      className="date-picker-input"
                      calendarClassName="custom-calendar"
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
        <div className={mode === 'PROFILE' ? "button-group questionnaire-profile-button-group" : "button-group"}>
          {mode === 'EXPLORE' && (
            <>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<SaveIcon />}
                onClick={onSecondaryAction}
                disabled={!canSubmit || loading}
                fullWidth={isMobile}
                className="update-profile-button"
              >
                {loading ? <CircularProgress size={24} /> : 'Save to Profile'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!canSubmit || loading}
                fullWidth={isMobile}
              >
                {loading ? <CircularProgress size={24} /> : 'Get Recommendations'}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={onReset}
                disabled={loading}
                fullWidth={isMobile}
              >
                Reset
              </Button>
              {toggleMapVisibility && isMobile && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={toggleMapVisibility}
                  disabled={!hasSuggestions}
                  fullWidth={isMobile}
                >
                  {isMapVisible ? 'Hide Map' : 'Show Map'}
                </Button>
              )}
            </>
          )}
          {mode === 'PROFILE' && (
            <>
              <BootstrapButton
                type="submit"
                disabled={loading}
                style={{
                  width: isMobile ? '100%' : 'auto',
                  backgroundColor: 'var(--btn-confirm-bg)',
                  borderColor: 'var(--btn-confirm-bg)',
                  color: '#fff'
                }}
              >
                {loading ? <Spinner animation="border" size="sm" /> : 'Save Preferences'}
              </BootstrapButton>
              <BootstrapButton
                onClick={onClose}
                disabled={loading}
                style={{
                  width: isMobile ? '100%' : 'auto',
                  marginLeft: '0.5rem',
                  backgroundColor: 'var(--btn-cancel-bg)',
                  borderColor: 'var(--btn-cancel-bg)',
                  color: '#fff'
                }}
              >
                Cancel
              </BootstrapButton>
            </>
          )}
        </div>
        {errorMessage && (
          <Alert severity="error" style={{ marginTop: '20px' }}>
            {errorMessage}
          </Alert>
        )}
      </form>
      {mode === 'EXPLORE' && suggestions && suggestions.length > 0 && (
        <div ref={suggestionSectionRef} className="suggestions-section">
          <Typography variant="h4" style={{ marginTop: '30px', marginBottom: '15px' }}>
            Suggestions:
          </Typography>
          {suggestions.map((suggestion, index) => (
            <SuggestionCard key={index} suggestion={suggestion} markerColor={suggestion.markerColor} />
          ))}
        </div>
      )}
    </div>
  );
};

export default QuestionnaireForm;
